import React, { ReactNode } from 'react';

// Utils
import Axios from "axios";
import { client } from '../../../tina/__generated__/client';
import { ProductDataModel } from '@brz-types/product.data.model';
import { useMatch } from '@reach/router';
import { getQueryParam } from '@brz-utils/get.query.param';
import { InsuranceLeadIDsMap, PRODUCT_IDS } from '@brz-types/lead.types';
import { env } from '@brz-env';
import { LinkDataModel } from '@brz-types/common/link.data.model';


type AgentContextModel = {
    agent: AgentDataModel | null | undefined;
    bga: BGADataModel | null | undefined;
};

const Context = React.createContext({} as AgentContextModel);

const endpoint = `${env.DEPLOYED_HOST}/app/api/agents/find/`;
const axios = Axios.create({ baseURL: endpoint });


export const AgentContextWrapper = ({ children }: { children: ReactNode | ReactNode[] }) => {
    const [agent, setAgent] = React.useState<AgentDataModel | null | undefined>(null);
    const [bga, setBGA] = React.useState<BGADataModel | null | undefined>(null);

    // Match path to separate Agent vs BGA logic
    const agentRouteMatch = useMatch('/agent/:url_slug');
    const bgaRouteMatch = useMatch('/bga/:url_slug');    
    

    const queryParams = {
        fname: getQueryParam('fname'),
        lname: getQueryParam('lname'),
        displayName: getQueryParam('displayName')
    };

    React.useEffect(() => {
        if ( agentRouteMatch ) {
            getAgentData();
            return;
        }

        if ( bgaRouteMatch ) {
            getBGAData();
            return;
        }

        if ( (queryParams.fname && queryParams.lname) || queryParams.displayName ) {
            setAgent({
                type: 'agent',
                id: null,
                firstName: queryParams.fname,
                lastName: queryParams.lname,
                websiteDisplayName: queryParams.displayName,
                products: null,
                urlSlug: null
            });
            return;
        }

        if ( !agentRouteMatch && !bgaRouteMatch ) {
            setAgent(undefined);
            setBGA(undefined);
        }

    }, []);


    const getAgentData = () => {
        if (!agentRouteMatch) return;

        axios.get(`${agentRouteMatch.url_slug}`).then((response) => {
            // console.log(response);

            if ( response.status === 200 ) {
                fetchAgentProductsByIds(response.data.products).then((products) => {
                    if ( products ) {
                        setAgent({
                            ...response.data,
                            type: 'agent',
                            products: products
                        });
                    }
                });
            } else {
                setAgent(undefined);
            }

        }).catch(() => {
            setAgent(undefined);
        });
    };


    const getBGAData = async () => {
        if (!bgaRouteMatch) return;

        const { data } = await client.request<{
            bgaConnection: {
                edges: Array<{
                    node: {
                        logoSrc: string;
                        mobileLogoSrc: string;
                        websiteDisplayName: string;
                        urlSlug: string;
                        productsRefs: Array<{
                            productRef: { shortName: string }
                        }> | null;
                        ctaLink: string;
                    }
                }>
            }
        }>({
            query: `
                query Bga($filter: BgaFilter) {
                    bgaConnection(filter: $filter) {
                        edges {
                            node {
                                urlSlug
                                websiteDisplayName
                                logoSrc
                                mobileLogoSrc
                                productsRefs {
                                    productRef {
                                        ... on Product {
                                            shortName
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                filter: { urlSlug: { eq: bgaRouteMatch.url_slug } }
            }
        });

        
        
        const found = data.bgaConnection.edges?.find((_, index) => index === 0)?.node;

        if ( found ) {
            const shortNames = found.productsRefs?.map(product => product.productRef.shortName) || null;
            const bgaData: BGADataModel = {
                type: 'bga',
                urlSlug: found.urlSlug,
                websiteDisplayName: found.websiteDisplayName,
                logo: found.logoSrc,
                mobileLogo: found.mobileLogoSrc,
                ctaLink: found.ctaLink,
                products: null
            };

            if ( shortNames ) {
                fetchProducts(shortNames).then((products) => {
                    setBGA({
                        ...bgaData,
                        products
                    })
                }).catch(() => {
                    setBGA(bgaData);
                });
            } else {
                setBGA(bgaData);
            }

        } else {
            setBGA(undefined);
        }
    };
    
    

    return (
        <Context.Provider value={{
            agent,
            bga
        }}>
            { children }
        </Context.Provider>
    )
};

export const useAgent = () => React.useContext(Context);


export const fetchAgentProductsByIds = async (ids: Array<number>): Promise<Array<ProductDataModel> | null> => {
    const products = Object.keys(InsuranceLeadIDsMap).map((key) => {
        const values = InsuranceLeadIDsMap[key as keyof typeof InsuranceLeadIDsMap];
        return values.map(value => {
            return ids.includes(value) ? key : null;
        });
    });

    const clean = products.flat().filter(product => product !== null);
    const unique = clean.filter((product, index) => clean.indexOf(product) === index);
    

    return await fetchProducts(unique as Array<string>);
};


export const fetchProducts = async (shortNames: Array<string>): Promise<Array<ProductDataModel> | null> => {
    const { data } = await client.request({
        query: `
            query Manager($filter: ProductFilter) {
                productConnection(filter: $filter) {
                    edges {
                        node {
                            shortName
                            title
                            icon
                            disclaimer
                            descriptionText
                            buttons {
                                agentLink
                                label
                                link
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            filter: { shortName: { in: shortNames } }
        }
    });

    if ( data.productConnection.edges ) {
        return data.productConnection.edges.map((edge: any) => {
            return edge.node;
        });
    }

    return null;
};


export type AgentDataModel = {
    type: 'agent',
    id: number | null;
    firstName: string | null;
    lastName: string | null;
    websiteDisplayName: string | null;
    urlSlug: string | null;
    products: Array<ProductDataModel> | null;
};

export type BGADataModel = {
    type: 'bga',
    logo: string | null;
    mobileLogo: string | null;
    websiteDisplayName: string | null;
    urlSlug: string;
    products: Array<ProductDataModel> | null;
    ctaLink: string | null;
};


export const isAgent = (data: any): data is AgentDataModel => {
    return data.type === 'agent';
}; 


export const isBGA = (data: any): data is BGADataModel => {
    return data.type === 'bga';
}; 

